var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import BigNumber from 'bignumber.js';
import { Portfolio, AvailableLiquidity, PositionCard, MarketTable, TokenActionModal, NotificationComp, useWindowSizes, getAutoSlippage, FilterTab, SearchInput, } from 'ui-kit';
import { getIcon } from '../utils/getIcon';
import { generateSupplyTooltipItems, generateBorrowTooltipItems } from '../utils/tooltipItems';
import { usePosition } from '../context/PositionContext';
import { useAlphalendEventSubscription } from '../hooks/useAlphalendEventSubscription';
import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { marketsArray, getActiveMarkets } from '../data/marketInfo';
import { useBalances } from '../context/BalanceContext';
import { alphalendSwapAndSupplyOrRepayTransactionHandler, alphalendTransactionHandler, alphalendFlashRepayTransactionHandler, advancedClaimRewardsTransactionHandler, } from '../utils/alphalendTransactionHandler';
import { toRepayAmountBaseUnits, fetchNaviFlashLoanSupportedSet, getMaxRepayByCollateral, } from '../utils/flashRepayUtils';
import { useFlashRepaySupported } from '../hooks/useFlashRepaySupported';
import { useNaviFlashLoanFee } from '../hooks/useNaviFlashLoanFee';
import { useAddressBalanceGuard } from '../hooks/useAddressBalanceGuard';
import { getSuppliedAndBorrowedCoinTypes, calculateTransactionAmount, isSwapNeeded, getBorrowedCoinTypes, } from '../utils/zapSwapUtils';
import { usePollingSwapQuote } from '../hooks/usePollingSwapQuote';
import { useCurrentWallet, useSignAndExecuteTransaction } from '../hooks/walletCompat';
import { alphalendWebSocketService } from '../services/alphalendWebsocket';
import SwapModal from '../components/SwapModal';
import { SwapButton } from '../components/SwapButton';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlphalendClient } from 'alphalend-sdk';
import { getSuiGraphqlUrl, getSuiGrpcOptions } from '../utils/rpcEndpoints';
import { wbtc_layer0_coinType } from '../utils/coinDisplayUtils';
import { isMarketBorrowCapReached, isMarketSupplyCapReached } from '../utils/marketCapUtils';
import { IS_WIND_DOWN_ACTIVE, WIND_DOWN_MESSAGE } from '../utils/windDownConfig';
import { useSubscription } from '../hooks/useSubscription';
import { buildTokensList } from '../utils/walletBalanceHelpers';
// Token to CoinType mapping for swap operations
const TOKEN_TO_COINTYPE = {
    USDC: '0xdba34672e30cb065b1f93e3ab55318768fd6fef66c15942c9f7cb846e2f900e7::usdc::USDC',
    suiUSDT: '0x375f70cf2ae4c00bf37117d0c85a2c71545e6ee05c4a5c7d282cd66a4504b068::usdt::USDT',
    stSUI: '0xd1b72982e40348d069bb1ff701e634c117bb5f741f44dff91e472d3b01461e55::stsui::STSUI',
    SUI: '0x2::sui::SUI',
};
const AVAILABLE_TABS = ['All', 'SUI', 'Stables', 'BTC'];
const calculateWalletBalance = (walletBalancesMap, coinType, expo, price) => {
    if (!walletBalancesMap) {
        return { inWallet: '0', inWalletUSD: '0' };
    }
    const rawBalance = parseFloat(walletBalancesMap.get(coinType) || '0');
    const normalizedBalance = rawBalance / Math.pow(10, expo);
    const inWallet = normalizedBalance.toString();
    const inWalletUSD = (normalizedBalance * parseFloat(price)).toString();
    return { inWallet, inWalletUSD };
};
const calculateTotalBorrowed = (totalSupply, availableLiquidity) => {
    return (parseFloat(totalSupply || '0') - parseFloat(availableLiquidity || '0')).toString();
};
const createSortHandler = (setSortConfig) => {
    return (key) => {
        setSortConfig((prev) => {
            if (prev.key !== key) {
                return { key, direction: 'asc' };
            }
            if (prev.direction === null) {
                return { key, direction: 'asc' };
            }
            if (prev.direction === 'asc') {
                return { key, direction: 'desc' };
            }
            return { key, direction: null };
        });
    };
};
const sortTokens = (tokens, sortConfig) => {
    if (!sortConfig.direction)
        return tokens;
    return [...tokens].sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
        if (aValue === undefined || bValue === undefined)
            return 0;
        let comparison = 0;
        if (typeof aValue === 'string' && typeof bValue === 'string') {
            if (sortConfig.key === 'symbol') {
                comparison = aValue.localeCompare(bValue);
            }
            else {
                comparison = parseFloat(aValue) - parseFloat(bValue);
            }
        }
        else if (typeof aValue === 'number' && typeof bValue === 'number') {
            comparison = aValue - bValue;
        }
        return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
};
const getThemeIcon = (themeName, darkIcon, lightIcon) => {
    return themeName === 'dark' ? getIcon(darkIcon) : getIcon(lightIcon);
};
const Markets = ({ walletConnectFunctionality, themeName, address }) => {
    var _a, _b;
    const { selectedPositionCapId: positionCapId } = usePosition();
    const { walletBalancesMap } = useBalances();
    const checkAddressBalanceConflict = useAddressBalanceGuard();
    const { currentWallet } = useCurrentWallet();
    const signAndExecuteTransaction = useSignAndExecuteTransaction();
    const navigate = useNavigate();
    const { data: coinInfoData } = useSubscription('coin_info');
    const [tokens, setTokens] = useState([]);
    const tokensRef = useRef([]);
    const [supplySortConfig, setSupplySortConfig] = useState({ key: 'symbol', direction: null });
    const [borrowSortConfig, setBorrowSortConfig] = useState({ key: 'symbol', direction: null });
    const [modalState, setModalState] = useState({
        isOpen: false,
        token: null,
        actionType: null,
    });
    const [searchParams, setSearchParams] = useSearchParams();
    const getInitialTab = () => {
        const tabParam = searchParams.get('tab');
        if (tabParam) {
            const matchedTab = AVAILABLE_TABS.find((tab) => tab.toLowerCase() === tabParam.toLowerCase());
            return matchedTab || 'All';
        }
        return 'All';
    };
    const [activeNavTab, setActiveNavTab] = useState(getInitialTab());
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState('Supply');
    const [isTransactionLoading, setIsTransactionLoading] = useState(false);
    const [resetInputTrigger, setResetInputTrigger] = useState(0);
    const [isNotificationOpen, setIsNotificationOpen] = useState(false);
    const [isUserRejectionError, setIsUserRejectionError] = useState(false);
    const [notificationInfo, setNotificationInfo] = useState({
        title: '',
        description: '',
        link: '',
        image: undefined,
        spinner: false,
    });
    const [isSwapModalOpen, setIsSwapModalOpen] = useState(false);
    const [slippageError, setSlippageError] = useState(null);
    const [isValidatingSlippage, setIsValidatingSlippage] = useState(false);
    const [lastValidatedToken, setLastValidatedToken] = useState(null);
    const slippageIntervalRef = useRef(null);
    const [selectedSwapSupplyToken, setSelectedSwapSupplyToken] = useState(null);
    const [swapAndPayAmount, setSwapAndPayAmount] = useState('');
    const [swapQuoteObject, setSwapQuoteObject] = useState(null);
    const [slippageValue, setSlippageValue] = useState(0.5);
    const [isQuoteStale, setIsQuoteStale] = useState(false);
    const [isQuoteLoading, setIsQuoteLoading] = useState(false);
    const [selectedCollateralToken, setSelectedCollateralToken] = useState(null);
    // Flash repay quote states
    const [flashRepayQuoteObject, setFlashRepayQuoteObject] = useState(null);
    const [isFlashRepayQuoteStale, setIsFlashRepayQuoteStale] = useState(false);
    const [isFlashRepayQuoteLoading, setIsFlashRepayQuoteLoading] = useState(false);
    const [flashRepaySwapAmount, setFlashRepaySwapAmount] = useState('');
    const [useCollateral, setUseCollateral] = useState(false);
    useEffect(() => {
        const tabParam = searchParams.get('tab');
        if (tabParam) {
            const matchedTab = AVAILABLE_TABS.find((tab) => tab.toLowerCase() === tabParam.toLowerCase());
            if (matchedTab) {
                setActiveNavTab(matchedTab);
            }
        }
        else {
            setActiveNavTab('All');
        }
    }, [searchParams]);
    useEffect(() => {
        const savedScrollPosition = sessionStorage.getItem('marketsPageScrollPosition');
        if (savedScrollPosition) {
            window.scrollTo(0, parseInt(savedScrollPosition, 10));
            sessionStorage.removeItem('marketsPageScrollPosition');
        }
    }, []);
    useEffect(() => {
        const handleScroll = () => {
            sessionStorage.setItem('marketsPageScrollPosition', window.scrollY.toString());
        };
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);
    const { data: portfolioHighFreqData } = useAlphalendEventSubscription(positionCapId ? `portfolio:${positionCapId}` : null, positionCapId ? `portfolio:${positionCapId}_high_freq` : null);
    const { data: portfolioLowFreqData } = useAlphalendEventSubscription(positionCapId ? `portfolio:${positionCapId}` : null, positionCapId ? `portfolio:${positionCapId}_low_freq` : null);
    const { data: marketHighFreqData } = useAlphalendEventSubscription('market_data', 'market_data_high_freq');
    const { data: marketLowFreqData } = useAlphalendEventSubscription('market_data', 'market_data_low_freq');
    const screenWidth = useWindowSizes();
    const repayMarket = modalState.isOpen && modalState.actionType === 'Repay' && modalState.token
        ? marketsArray.find((m) => { var _a; return m.id === ((_a = modalState.token) === null || _a === void 0 ? void 0 : _a.marketId); })
        : undefined;
    useEffect(() => {
        const borrowedAmounts = portfolioLowFreqData === null || portfolioLowFreqData === void 0 ? void 0 : portfolioLowFreqData.borrowed_amounts;
        if (!borrowedAmounts || Object.keys(borrowedAmounts).length === 0)
            return;
        fetchNaviFlashLoanSupportedSet().catch(() => { });
    }, [portfolioLowFreqData === null || portfolioLowFreqData === void 0 ? void 0 : portfolioLowFreqData.borrowed_amounts]);
    const flashRepayFromHook = useFlashRepaySupported(repayMarket === null || repayMarket === void 0 ? void 0 : repayMarket.coinType);
    const isFlashRepaySupported = modalState.isOpen &&
        modalState.actionType === 'Repay' &&
        modalState.token &&
        !(repayMarket === null || repayMarket === void 0 ? void 0 : repayMarket.coinType)
        ? false
        : flashRepayFromHook;
    const { feeDecimal: naviFlashLoanFee } = useNaviFlashLoanFee(repayMarket === null || repayMarket === void 0 ? void 0 : repayMarket.coinType);
    const flashRepayMaxRepayAmount = useMemo(() => {
        const t = modalState.token;
        if (!(t === null || t === void 0 ? void 0 : t.price) || !(t === null || t === void 0 ? void 0 : t.borrowedBalance))
            return undefined;
        return getMaxRepayByCollateral(selectedCollateralToken !== null && selectedCollateralToken !== void 0 ? selectedCollateralToken : undefined, t.price, t.borrowedBalance, naviFlashLoanFee);
    }, [modalState.token, selectedCollateralToken, naviFlashLoanFee]);
    // Memoize AlphalendClient to avoid recreating on every validation
    const alphalendClient = useMemo(() => {
        return new AlphalendClient('mainnet', getSuiGraphqlUrl('mainnet'), getSuiGrpcOptions('mainnet'));
    }, []);
    // Memoize market lookup maps for O(1) access
    const marketsByCoinType = useMemo(() => {
        const map = new Map();
        marketsArray.forEach((market) => {
            map.set(market.coinType.toLowerCase(), market);
        });
        return map;
    }, []);
    const prepareTokenForModal = (token) => {
        if (!token) {
            return {
                symbol: '',
                icon: '',
                price: '0',
                marketId: '',
                isSupplyEnabled: false,
                isBorrowEnabled: false,
            };
        }
        const marketToken = allMarketTokens.find((t) => t.marketId === token.marketId);
        return Object.assign(Object.assign({}, token), { walletBalance: token.inWallet || '0', totalBorrowedUsd: token.borrowedBalanceUSD || '0', totalSupplyUsd: token.supplyBalanceUSD || '0', supplyToolTipItems: token.supplyToolTipItems || (marketToken === null || marketToken === void 0 ? void 0 : marketToken.supplyToolTipItems), borrowToolTipItems: token.borrowToolTipItems || (marketToken === null || marketToken === void 0 ? void 0 : marketToken.borrowToolTipItems) });
    };
    const resetQuoteData = useCallback(() => {
        setSwapQuoteObject(null);
        setSwapAndPayAmount('');
    }, []);
    const createModalHandler = (actionType) => (token) => {
        // Reset quote data whenever modal opens with a token
        resetQuoteData();
        setSelectedSwapSupplyToken(null);
        const marketData = token;
        setModalState({
            isOpen: true,
            token: marketData,
            actionType,
        });
        setActiveTab(actionType);
    };
    const handleSupply = createModalHandler('Supply');
    const handleBorrow = createModalHandler('Borrow');
    const handleWithdraw = createModalHandler('Withdraw');
    const handleRepay = createModalHandler('Repay');
    const handleCloseModal = () => {
        if (isUserRejectionError && isNotificationOpen) {
            return;
        }
        setModalState({
            isOpen: false,
            token: null,
            actionType: null,
        });
        setResetInputTrigger((prev) => prev + 1);
        setSelectedSwapSupplyToken(null);
        resetQuoteData();
    };
    const prevActiveTab = useRef(null);
    useEffect(() => {
        if (prevActiveTab.current && prevActiveTab.current !== activeTab) {
            resetQuoteData();
            setSelectedSwapSupplyToken(null);
        }
        prevActiveTab.current = activeTab;
    }, [activeTab, resetQuoteData, modalState.token]);
    const handleNotificationClose = (value) => {
        const isOpen = typeof value === 'function' ? value(isNotificationOpen) : value;
        setIsNotificationOpen(isOpen);
        if (!isOpen) {
            setIsUserRejectionError(false);
        }
    };
    const handleSwapNotification = (info) => {
        setNotificationInfo({
            title: info.title,
            description: info.description,
            spinner: info.spinner || false,
            link: info.link || '',
            image: info.image || undefined,
        });
        setIsNotificationOpen(true);
    };
    // Update tokensRef whenever tokens changes
    useEffect(() => {
        tokensRef.current = tokens;
    }, [tokens]);
    // Get default token for swap
    const defaultTokenForSwap = useMemo(() => {
        if (!modalState.token)
            return undefined;
        const marketInfo = marketsArray.find((m) => { var _a; return m.id === ((_a = modalState.token) === null || _a === void 0 ? void 0 : _a.marketId); });
        if (!marketInfo)
            return undefined;
        return tokens.find((t) => (t.symbol !== 'SUI' && t.tokenId === marketInfo.coinType) ||
            (t.symbol === 'SUI' && marketInfo.coinType === '0x2::sui::SUI'));
    }, [tokens, modalState.token]);
    // Determine if polling should be enabled
    const isPollingEnabled = useMemo(() => {
        return !!(defaultTokenForSwap &&
            selectedSwapSupplyToken &&
            defaultTokenForSwap.symbol !== selectedSwapSupplyToken.symbol &&
            parseFloat(swapAndPayAmount) > 0 &&
            (activeTab === 'Supply' || activeTab === 'Repay') &&
            modalState.isOpen);
    }, [
        defaultTokenForSwap,
        selectedSwapSupplyToken,
        swapAndPayAmount,
        activeTab,
        modalState.isOpen,
    ]);
    // Use polling hook for swap quotes
    const { newQuoteArrived } = usePollingSwapQuote({
        payAmount: swapAndPayAmount,
        payToken: selectedSwapSupplyToken,
        receiveToken: defaultTokenForSwap || null,
        slippageValue,
        enabled: isPollingEnabled,
        onQuoteUpdate: (quote) => {
            setSwapQuoteObject(quote);
            setIsQuoteStale(false); // Clear stale flag when new quote arrives
        },
        onLoadingChange: (loading) => {
            setIsQuoteLoading(loading);
        },
        // Don't set transaction loading for swap quote fetching - it's not a transaction
        currentQuotePayAmount: swapQuoteObject === null || swapQuoteObject === void 0 ? void 0 : swapQuoteObject.payAmount,
    });
    // Mark quote as stale when amount changes, or clear when amount is 0/empty
    useEffect(() => {
        if (!swapAndPayAmount || parseFloat(swapAndPayAmount) <= 0) {
            setSwapQuoteObject(null);
            setIsQuoteStale(false);
            setIsQuoteLoading(false);
        }
        else if (swapQuoteObject && swapQuoteObject.payAmount !== swapAndPayAmount) {
            // Mark old quote as stale to blur it while new quote is being fetched
            setIsQuoteStale(true);
        }
    }, [swapAndPayAmount, swapQuoteObject]);
    // Flash repay quote polling
    const isFlashRepayPollingEnabled = useMemo(() => {
        return !!(useCollateral &&
            selectedCollateralToken &&
            defaultTokenForSwap &&
            selectedCollateralToken.symbol !== defaultTokenForSwap.symbol &&
            parseFloat(flashRepaySwapAmount) > 0 &&
            activeTab === 'Repay' &&
            modalState.isOpen);
    }, [
        useCollateral,
        selectedCollateralToken,
        defaultTokenForSwap,
        flashRepaySwapAmount,
        activeTab,
        modalState.isOpen,
    ]);
    const collateralAsPayToken = useMemo(() => {
        if (!selectedCollateralToken)
            return null;
        const balance = parseFloat(selectedCollateralToken.balance || '0');
        const price = selectedCollateralToken.usdValueNum != null && balance > 0
            ? (selectedCollateralToken.usdValueNum / balance).toString()
            : '0';
        return {
            symbol: selectedCollateralToken.symbol,
            icon: selectedCollateralToken.icon,
            walletBalance: selectedCollateralToken.balance,
            price,
            // coinType is what fetchSwapQuote uses as tokenId — must be the full coin type, not symbol
            tokenId: selectedCollateralToken.coinType || selectedCollateralToken.symbol,
            // expo is required so fetchSwapQuote correctly normalizes to base units
            decimals: selectedCollateralToken.expo,
        };
    }, [selectedCollateralToken]);
    const { newQuoteArrived: flashRepayNewQuoteArrived } = usePollingSwapQuote({
        payAmount: flashRepaySwapAmount,
        payToken: collateralAsPayToken,
        receiveToken: defaultTokenForSwap || null,
        slippageValue,
        enabled: isFlashRepayPollingEnabled,
        onQuoteUpdate: (quote) => {
            setFlashRepayQuoteObject(quote);
            setIsFlashRepayQuoteStale(false);
        },
        onLoadingChange: (loading) => {
            setIsFlashRepayQuoteLoading(loading);
        },
        currentQuotePayAmount: flashRepayQuoteObject === null || flashRepayQuoteObject === void 0 ? void 0 : flashRepayQuoteObject.payAmount,
    });
    useEffect(() => {
        if (!flashRepaySwapAmount || parseFloat(flashRepaySwapAmount) <= 0) {
            setFlashRepayQuoteObject(null);
            setIsFlashRepayQuoteStale(false);
            setIsFlashRepayQuoteLoading(false);
        }
        else if (flashRepayQuoteObject) {
            const quotedAmt = new BigNumber(flashRepayQuoteObject.payAmount);
            const currentAmt = new BigNumber(flashRepaySwapAmount);
            if (!quotedAmt.isEqualTo(currentAmt)) {
                setIsFlashRepayQuoteStale(true);
            }
        }
    }, [flashRepaySwapAmount, flashRepayQuoteObject]);
    useEffect(() => {
        if (!useCollateral) {
            setFlashRepayQuoteObject(null);
            setFlashRepaySwapAmount('');
            setIsFlashRepayQuoteStale(false);
            setIsFlashRepayQuoteLoading(false);
        }
    }, [useCollateral, selectedCollateralToken]);
    // Handle token selection and reset slippage
    const handlePayTokenSelect = (token) => {
        var _a, _b;
        if (!token)
            return;
        setSelectedSwapSupplyToken(token);
        setSwapAndPayAmount('');
        setSlippageValue(getAutoSlippage(token.symbol, (_b = (_a = modalState.token) === null || _a === void 0 ? void 0 : _a.symbol) !== null && _b !== void 0 ? _b : undefined));
        resetQuoteData();
    };
    const handleSubmit = (amount, action, displayAmount, selectedToken, options) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
        if (!address || !modalState.token) {
            console.error('Missing required data for transaction');
            return;
        }
        // Wind-down mode: never build a supply or borrow transaction.
        if (IS_WIND_DOWN_ACTIVE && (action === 'Supply' || action === 'Borrow')) {
            console.error(`${action} is paused while AlphaFi winds down`);
            return;
        }
        if (action === 'Supply' || action === 'Repay') {
            const marketInfo = marketsArray.find((m) => { var _a; return m.id === ((_a = modalState.token) === null || _a === void 0 ? void 0 : _a.marketId); });
            if (checkAddressBalanceConflict([
                { coinType: selectedToken === null || selectedToken === void 0 ? void 0 : selectedToken.tokenId },
                { coinType: marketInfo === null || marketInfo === void 0 ? void 0 : marketInfo.coinType },
            ])) {
                setNotificationInfo({
                    title: 'Wallet Update Required',
                    description: `${(_a = currentWallet === null || currentWallet === void 0 ? void 0 : currentWallet.name) !== null && _a !== void 0 ? _a : 'Your wallet'} doesn't yet support address balances, a recent Sui network update. Your funds are safe — to continue, connect a supported wallet.`,
                    image: getIcon('failed'),
                    spinner: false,
                });
                setIsNotificationOpen(true);
                return;
            }
        }
        const suiBalance = (walletBalancesMap === null || walletBalancesMap === void 0 ? void 0 : walletBalancesMap.get('0x2::sui::SUI')) || '0';
        const suppliedAndBorrowedCoinTypes = getSuppliedAndBorrowedCoinTypes(portfolioLowFreqData, (_b = modalState.token) === null || _b === void 0 ? void 0 : _b.marketId);
        const borrowedCoinTypes = getBorrowedCoinTypes(portfolioLowFreqData);
        const repayMarketInfo = marketsArray.find((m) => { var _a; return m.id === ((_a = modalState.token) === null || _a === void 0 ? void 0 : _a.marketId); });
        if (action === 'Repay' &&
            (options === null || options === void 0 ? void 0 : options.useCollateral) &&
            ((_c = options === null || options === void 0 ? void 0 : options.selectedCollateralToken) === null || _c === void 0 ? void 0 : _c.marketId) &&
            ((_d = options === null || options === void 0 ? void 0 : options.selectedCollateralToken) === null || _d === void 0 ? void 0 : _d.coinType) &&
            positionCapId &&
            repayMarketInfo) {
            // Cap at max flash repay (borrowed and collateral limit) so we never repay more than allowed
            const numAmount = parseFloat(amount);
            const numMax = flashRepayMaxRepayAmount != null && flashRepayMaxRepayAmount !== ''
                ? parseFloat(flashRepayMaxRepayAmount)
                : Infinity;
            const cappedAmount = Number.isFinite(numAmount) && Number.isFinite(numMax)
                ? String(Math.min(numAmount, numMax))
                : amount;
            // When repaying "max" (amount close to borrowed), omit repayAmountBaseUnits so SDK uses exact on-chain debt and no dust remains.
            const borrowedStr = (_f = (_e = modalState.token) === null || _e === void 0 ? void 0 : _e.borrowedBalance) !== null && _f !== void 0 ? _f : '';
            const borrowedNum = parseFloat(borrowedStr);
            const isMaxRepay = borrowedStr &&
                Number.isFinite(borrowedNum) &&
                borrowedNum > 0 &&
                parseFloat(cappedAmount) >= borrowedNum * 0.9999;
            const amountToRepay = isMaxRepay ? borrowedStr : cappedAmount;
            const repayAmountBaseUnits = isMaxRepay
                ? undefined
                : toRepayAmountBaseUnits(amountToRepay, repayMarketInfo.expo);
            const slippageDecimal = Number.isFinite(slippageValue) && slippageValue > 0
                ? Math.min(slippageValue / 100, 1)
                : 0.01;
            try {
                yield alphalendFlashRepayTransactionHandler({
                    positionCapId,
                    repayCoinType: repayMarketInfo.coinType,
                    repayMarketId: modalState.token.marketId,
                    withdrawCoinType: options.selectedCollateralToken.coinType,
                    withdrawMarketId: options.selectedCollateralToken.marketId,
                    address,
                    slippage: slippageDecimal,
                    network: 'mainnet',
                    repayAmountBaseUnits,
                    signAndExecuteTransaction: signAndExecuteTransaction,
                    setNotificationInfo: setNotificationInfo,
                    setIsNotificationOpen: setIsNotificationOpen,
                    setIsTransactionLoading: setIsTransactionLoading,
                    resetInputValues: () => {
                        setResetInputTrigger((prev) => prev + 1);
                        handleCloseModal();
                    },
                    notifyTransaction: (marketId, posCapId, txType) => {
                        alphalendWebSocketService.sendTransactionNotification(marketId, posCapId, txType);
                    },
                    notificationContext: {
                        repaySymbol: (_j = (_g = repayMarketInfo.symbol) !== null && _g !== void 0 ? _g : (_h = modalState.token) === null || _h === void 0 ? void 0 : _h.symbol) !== null && _j !== void 0 ? _j : 'debt',
                        withdrawSymbol: (_k = options.selectedCollateralToken.symbol) !== null && _k !== void 0 ? _k : 'collateral',
                        repayDisplayAmount: displayAmount,
                        isPartial: repayAmountBaseUnits != null,
                    },
                });
            }
            catch (err) {
                setIsTransactionLoading(false);
                setNotificationInfo({
                    title: 'Flash Repay Failed',
                    description: (_l = err === null || err === void 0 ? void 0 : err.message) !== null && _l !== void 0 ? _l : 'Failed to build or submit flash repay.',
                    spinner: false,
                });
                setIsNotificationOpen(true);
            }
            return;
        }
        const transaction_amount = calculateTransactionAmount(swapQuoteObject, selectedToken);
        let transaction_displayAmount = displayAmount;
        const marketInfo = marketsArray.find((m) => { var _a; return m.id === ((_a = modalState.token) === null || _a === void 0 ? void 0 : _a.marketId); });
        const defaultToken = marketInfo
            ? tokensRef.current.find((t) => (t.symbol !== 'SUI' && t.tokenId === marketInfo.coinType) ||
                (t.symbol === 'SUI' && marketInfo.coinType === '0x2::sui::SUI'))
            : undefined;
        if (isSwapNeeded(selectedToken, defaultToken, swapQuoteObject)) {
            transaction_displayAmount = (swapQuoteObject === null || swapQuoteObject === void 0 ? void 0 : swapQuoteObject.payAmount) || '0';
            yield alphalendSwapAndSupplyOrRepayTransactionHandler({
                amount: transaction_amount,
                network: 'mainnet',
                action: activeTab === 'Supply' ? 'swap_supply' : 'swap_repay',
                modalState: modalState,
                address: address,
                positionCapId: positionCapId,
                signAndExecuteTransaction: signAndExecuteTransaction,
                setNotificationInfo: setNotificationInfo,
                setIsNotificationOpen: setIsNotificationOpen,
                setIsTransactionLoading: setIsTransactionLoading,
                displayAmount: transaction_displayAmount,
                receivedAmountDisplay: swapQuoteObject === null || swapQuoteObject === void 0 ? void 0 : swapQuoteObject.receiveAmount,
                suiBalance: suiBalance,
                swappedFromTokenCoinType: (selectedToken === null || selectedToken === void 0 ? void 0 : selectedToken.tokenId) || '',
                swappedFromTokenSymbol: (selectedToken === null || selectedToken === void 0 ? void 0 : selectedToken.symbol) || '',
                swappedIntoTokenCoinType: (swapQuoteObject === null || swapQuoteObject === void 0 ? void 0 : swapQuoteObject.receiveToken.tokenId) || '',
                slippage: slippageValue,
                resetInputValues: () => {
                    setResetInputTrigger((prev) => prev + 1);
                    setSwapQuoteObject(null);
                    setSwapAndPayAmount('');
                    handleCloseModal();
                },
                notifyTransaction: (marketId, positionCapId, txType) => {
                    alphalendWebSocketService.sendTransactionNotification(marketId, positionCapId, txType);
                },
                suppliedAndBorrowedCoinTypes: suppliedAndBorrowedCoinTypes,
                path: window.location.pathname,
            });
            return;
        }
        yield alphalendTransactionHandler({
            amount: amount,
            network: 'mainnet',
            action: action,
            modalState: modalState,
            address: address,
            positionCapId: positionCapId,
            signAndExecuteTransaction: signAndExecuteTransaction,
            setNotificationInfo: setNotificationInfo,
            setIsNotificationOpen: setIsNotificationOpen,
            setIsTransactionLoading: setIsTransactionLoading,
            displayAmount: transaction_displayAmount,
            setIsUserRejectionError: setIsUserRejectionError,
            suiBalance: suiBalance,
            resetInputValues: () => {
                setResetInputTrigger((prev) => prev + 1);
                handleCloseModal();
            },
            notifyTransaction: (marketId, positionCapId, txType) => {
                alphalendWebSocketService.sendTransactionNotification(marketId, positionCapId, txType);
            },
            suppliedAndBorrowedCoinTypes: suppliedAndBorrowedCoinTypes,
            path: window.location.pathname,
            borrowedCoinTypes: borrowedCoinTypes,
        });
    });
    // Validate slippage before claim rewards with swap
    const handleValidateSlippage = (selectedToken_1, ...args_1) => __awaiter(void 0, [selectedToken_1, ...args_1], void 0, function* (selectedToken, showLoader = true) {
        var _a, _b, _c;
        if (!address || !(portfolioHighFreqData === null || portfolioHighFreqData === void 0 ? void 0 : portfolioHighFreqData.rewards_by_token)) {
            if (showLoader)
                setIsValidatingSlippage(false);
            return;
        }
        const targetCoinType = TOKEN_TO_COINTYPE[selectedToken];
        if (!targetCoinType) {
            setSlippageError('Unknown target token');
            if (showLoader)
                setIsValidatingSlippage(false);
            return;
        }
        if (showLoader)
            setIsValidatingSlippage(true);
        setSlippageError(null);
        try {
            const rewardsByToken = portfolioHighFreqData.rewards_by_token;
            // Check if there are any swappable rewards (>= $0.01)
            let hasSwappableRewards = false;
            for (const [coinType, amount] of Object.entries(rewardsByToken)) {
                const rewardAmount = parseFloat(amount);
                if (rewardAmount <= 0)
                    continue;
                const marketInfo = marketsByCoinType.get(coinType.toLowerCase());
                if (!marketInfo)
                    continue;
                const marketData = (_a = marketHighFreqData === null || marketHighFreqData === void 0 ? void 0 : marketHighFreqData.markets) === null || _a === void 0 ? void 0 : _a[marketInfo.id];
                const tokenPrice = (marketData === null || marketData === void 0 ? void 0 : marketData.price) ? parseFloat(marketData.price) : 0;
                const usdValue = rewardAmount * tokenPrice;
                if (usdValue >= 0.01) {
                    hasSwappableRewards = true;
                    break;
                }
            }
            if (!hasSwappableRewards) {
                setSlippageError(null);
                if (showLoader)
                    setIsValidatingSlippage(false);
                return;
            }
            const targetMarket = marketsByCoinType.get(targetCoinType.toLowerCase());
            if (!targetMarket) {
                setSlippageError(null);
                if (showLoader)
                    setIsValidatingSlippage(false);
                return;
            }
            const targetMarketData = (_b = marketHighFreqData === null || marketHighFreqData === void 0 ? void 0 : marketHighFreqData.markets) === null || _b === void 0 ? void 0 : _b[targetMarket.id];
            const targetTokenPrice = (targetMarketData === null || targetMarketData === void 0 ? void 0 : targetMarketData.price) ? parseFloat(targetMarketData.price) : 0;
            if (targetTokenPrice === 0) {
                setSlippageError(null);
                if (showLoader)
                    setIsValidatingSlippage(false);
                return;
            }
            let totalNegativeSlippage = 0;
            let swapCount = 0;
            const targetCoinTypeLower = targetCoinType.toLowerCase();
            // Process all reward tokens that need swapping
            for (const [coinType, amount] of Object.entries(rewardsByToken)) {
                if (coinType.toLowerCase() === targetCoinTypeLower || parseFloat(amount) <= 0) {
                    continue;
                }
                const marketInfo = marketsByCoinType.get(coinType.toLowerCase());
                if (!marketInfo)
                    continue;
                const marketData = (_c = marketHighFreqData === null || marketHighFreqData === void 0 ? void 0 : marketHighFreqData.markets) === null || _c === void 0 ? void 0 : _c[marketInfo.id];
                const tokenPrice = (marketData === null || marketData === void 0 ? void 0 : marketData.price) ? parseFloat(marketData.price) : 0;
                if (tokenPrice === 0)
                    continue;
                const rewardAmount = parseFloat(amount);
                const usdValue = rewardAmount * tokenPrice;
                if (usdValue < 0.01)
                    continue;
                // Convert to base units and get quote
                const amountInBaseUnits = Math.floor(rewardAmount * Math.pow(10, marketInfo.expo)).toString();
                try {
                    const quote = yield alphalendClient['cetusSwap'].getCetusSwapQuote(marketInfo.coinType, targetCoinType, amountInBaseUnits);
                    if (!(quote === null || quote === void 0 ? void 0 : quote.amountOut) || !(quote === null || quote === void 0 ? void 0 : quote.amountIn))
                        continue;
                    // Calculate USD values for slippage
                    const inputUSD = (parseFloat(quote.amountIn.toString()) * tokenPrice) / Math.pow(10, marketInfo.expo);
                    const outputUSD = (parseFloat(quote.amountOut.toString()) * targetTokenPrice) /
                        Math.pow(10, targetMarket.expo);
                    if (inputUSD <= 0)
                        continue;
                    const slippagePercent = ((outputUSD - inputUSD) / inputUSD) * 100;
                    // Only accumulate negative slippage for threshold check
                    if (slippagePercent < 0) {
                        totalNegativeSlippage += Math.abs(slippagePercent);
                        swapCount++;
                    }
                }
                catch (error) {
                    console.error(`Error getting quote for ${marketInfo.coinType}:`, error);
                }
            }
            // Calculate average negative slippage
            const avgNegativeSlippage = swapCount > 0 ? totalNegativeSlippage / swapCount : 0;
            if (avgNegativeSlippage > 1) {
                setSlippageError('Cannot swap rewards. Slippage too high.');
                if (showLoader)
                    setIsValidatingSlippage(false);
                return;
            }
            setSlippageError(null);
            if (showLoader)
                setIsValidatingSlippage(false);
        }
        catch (error) {
            console.error('Error validating slippage:', error);
            setSlippageError('Failed to validate slippage');
            if (showLoader)
                setIsValidatingSlippage(false);
        }
    });
    const handleClaimRewards = (swapToToken, supplyOrRepay, selectedToken) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        if (swapToToken && slippageError) {
            setNotificationInfo({
                title: 'Slippage Too High',
                description: slippageError,
                image: getIcon('failed'),
            });
            setIsNotificationOpen(true);
            return;
        }
        if (!address || !positionCapId) {
            return;
        }
        if (swapToToken || supplyOrRepay) {
            const rewardsByToken = (_a = portfolioHighFreqData === null || portfolioHighFreqData === void 0 ? void 0 : portfolioHighFreqData.rewards_by_token) !== null && _a !== void 0 ? _a : {};
            const targetCoinType = selectedToken ? TOKEN_TO_COINTYPE[selectedToken] : undefined;
            if (checkAddressBalanceConflict([
                ...Object.entries(rewardsByToken).map(([ct]) => ({ coinType: ct })),
                { coinType: targetCoinType },
            ])) {
                setNotificationInfo({
                    title: 'Wallet Update Required',
                    description: `${(_b = currentWallet === null || currentWallet === void 0 ? void 0 : currentWallet.name) !== null && _b !== void 0 ? _b : 'Your wallet'} doesn't yet support address balances, a recent Sui network update. Your funds are safe — to continue, connect a supported wallet.`,
                    image: getIcon('failed'),
                    spinner: false,
                });
                setIsNotificationOpen(true);
                return;
            }
        }
        const suiBalance = (walletBalancesMap === null || walletBalancesMap === void 0 ? void 0 : walletBalancesMap.get('0x2::sui::SUI')) || '0';
        // Get borrowed amounts for repay logic
        const borrowedAmounts = new Map();
        if (portfolioLowFreqData === null || portfolioLowFreqData === void 0 ? void 0 : portfolioLowFreqData.borrowed_amounts) {
            Object.entries(portfolioLowFreqData.borrowed_amounts).forEach(([marketId, amount]) => {
                const market = marketsArray.find((m) => m.id === marketId);
                if (market) {
                    borrowedAmounts.set(market.coinType, amount);
                }
            });
        }
        // Get all supplied and borrowed coin types for price updates
        const suppliedAndBorrowedCoinTypes = [];
        if (portfolioLowFreqData === null || portfolioLowFreqData === void 0 ? void 0 : portfolioLowFreqData.supplied_amounts) {
            Object.keys(portfolioLowFreqData.supplied_amounts).forEach((marketId) => {
                const market = marketsArray.find((m) => m.id === marketId);
                if (market) {
                    suppliedAndBorrowedCoinTypes.push(market.coinType);
                }
            });
        }
        if (portfolioLowFreqData === null || portfolioLowFreqData === void 0 ? void 0 : portfolioLowFreqData.borrowed_amounts) {
            Object.keys(portfolioLowFreqData.borrowed_amounts).forEach((marketId) => {
                const market = marketsArray.find((m) => m.id === marketId);
                if (market && !suppliedAndBorrowedCoinTypes.includes(market.coinType)) {
                    suppliedAndBorrowedCoinTypes.push(market.coinType);
                }
            });
        }
        const rewardsDist = getRewardsDistribution();
        const rewardsTotalUsd = rewardsDist.reduce((sum, r) => sum + parseFloat(r.usdAmount || '0'), 0);
        const rewardsAnalytics = {
            totalUsd: `$${rewardsTotalUsd.toFixed(2)}`,
            rewards: rewardsDist
                .filter((r) => parseFloat(r.amount) > 0)
                .map((r) => `(${r.label},${parseFloat(r.amount).toFixed(5)})`),
        };
        yield advancedClaimRewardsTransactionHandler({
            address: address,
            positionCapId: positionCapId,
            network: 'mainnet',
            notifyTransaction: (positionCapId) => {
                alphalendWebSocketService.sendClaimRewardsNotification(positionCapId);
            },
            signAndExecuteTransaction: signAndExecuteTransaction,
            setNotificationInfo: setNotificationInfo,
            setIsNotificationOpen: setIsNotificationOpen,
            setIsTransactionLoading: setIsTransactionLoading,
            swapToToken: swapToToken,
            supplyOrRepay: supplyOrRepay,
            selectedToken: selectedToken,
            suiBalance: suiBalance,
            resetInputValues: undefined,
            suppliedAndBorrowedCoinTypes: suppliedAndBorrowedCoinTypes,
            borrowedAmounts: borrowedAmounts,
            rewardsByToken: portfolioHighFreqData === null || portfolioHighFreqData === void 0 ? void 0 : portfolioHighFreqData.rewards_by_token,
            marketsArray: marketsArray,
            rewardsAnalytics: rewardsAnalytics,
        });
    });
    const allMarketTokens = useMemo(() => {
        if (!marketHighFreqData || !marketLowFreqData) {
            return [];
        }
        const tokens = [];
        const activeMarkets = getActiveMarkets();
        activeMarkets.forEach((marketInfo) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j;
            const marketId = marketInfo.id;
            const marketHigh = marketHighFreqData.markets[marketId];
            const marketLow = (_a = marketLowFreqData.markets) === null || _a === void 0 ? void 0 : _a[marketId];
            if (!marketHigh || !marketLow) {
                return;
            }
            const price = marketHigh.price || '0';
            const supplyApr = ((_b = marketLow.supply_apr) === null || _b === void 0 ? void 0 : _b.total) || '0';
            const borrowApr = ((_c = marketLow.borrow_apr) === null || _c === void 0 ? void 0 : _c.total) || '0';
            // Normalize to token's decimal precision (expo) to eliminate floating point noise
            // that would otherwise appear as dust in balance arithmetic downstream
            const supplyBalance = new BigNumber(((_d = portfolioLowFreqData === null || portfolioLowFreqData === void 0 ? void 0 : portfolioLowFreqData.supplied_amounts) === null || _d === void 0 ? void 0 : _d[marketId]) || '0').toFixed(marketInfo.expo);
            const borrowedBalance = new BigNumber(((_e = portfolioLowFreqData === null || portfolioLowFreqData === void 0 ? void 0 : portfolioLowFreqData.borrowed_amounts) === null || _e === void 0 ? void 0 : _e[marketId]) || '0').toFixed(marketInfo.expo);
            const supplyToolTipItems = generateSupplyTooltipItems(marketLow.supply_apr, marketInfo.symbol);
            const borrowToolTipItems = generateBorrowTooltipItems(marketLow.borrow_apr, marketInfo.symbol);
            const borrowCapReached = isMarketBorrowCapReached(marketLow);
            const supplyCapReached = isMarketSupplyCapReached(marketLow);
            const hasSupplyPosition = parseFloat(supplyBalance) > 0;
            const hasBorrowPosition = parseFloat(borrowedBalance) > 0;
            tokens.push(Object.assign(Object.assign({ marketId, symbol: marketInfo.symbol, icon: getIcon(marketInfo.coinType === wbtc_layer0_coinType
                    ? 'wbtc_layer0'
                    : marketInfo.symbol.toLowerCase()), price, totalSupply: marketHigh.total_supply || '0', totalBorrowed: calculateTotalBorrowed(marketHigh.total_supply || '0', marketHigh.available_liquidity || '0'), available: marketHigh.available_liquidity || '0', supplyApr,
                borrowApr, ltv: marketLow.ltv || '0', borrowFee: marketLow.borrow_fee || '0', 
                // Wind-down mode closes supply and borrow everywhere. Withdraw and repay stay open.
                pausedTooltip: IS_WIND_DOWN_ACTIVE ? WIND_DOWN_MESSAGE : undefined, isSupplyEnabled: !IS_WIND_DOWN_ACTIVE && !hasBorrowPosition && !marketInfo.retired, isSupplyCapReached: !hasBorrowPosition && !marketInfo.retired && supplyCapReached, isBorrowEnabled: !IS_WIND_DOWN_ACTIVE && !hasSupplyPosition && !marketInfo.retired && !borrowCapReached, isBorrowCapReached: !hasSupplyPosition && !marketInfo.retired && borrowCapReached, supplyBalance,
                borrowedBalance, supplyBalanceUSD: (parseFloat(supplyBalance) * parseFloat(price)).toString(), borrowedBalanceUSD: (parseFloat(borrowedBalance) * parseFloat(price)).toString(), totalSupplySubText: (parseFloat(marketHigh.total_supply || '0') * parseFloat(price)).toFixed(2), availableSubText: (parseFloat(marketHigh.available_liquidity || '0') * parseFloat(price)).toFixed(2), supplyToolTipItems: supplyToolTipItems, borrowToolTipItems: borrowToolTipItems, isSupplyRewardsEnabled: ((_f = marketLow.supply_apr) === null || _f === void 0 ? void 0 : _f.rewards) && marketLow.supply_apr.rewards.length > 0, isBorrowRewardsEnabled: ((_g = marketLow.borrow_apr) === null || _g === void 0 ? void 0 : _g.rewards) && marketLow.borrow_apr.rewards.length > 0, allowedSupplyAmount: marketLow.allowed_supply_amount || '0', allowedBorrowAmount: marketLow.allowed_borrow_amount || '0', 
                // null means "no limit reported"; must not collapse to '0' or every market with a
                // disabled limiter would gate at zero.
                remainingInflowCapacity: (_h = marketHigh.remaining_inflow_capacity) !== null && _h !== void 0 ? _h : null, remainingOutflowCapacity: (_j = marketHigh.remaining_outflow_capacity) !== null && _j !== void 0 ? _j : null, borrowWeight: marketLow.borrow_weight || '0', liquidationThreshold: marketLow.liquidation_threshold || '0', spreadFee: marketLow.spread_fee || '0', utilizationRate: marketLow.utilization_rate || '0', interestRateKinks: marketLow.interest_rate_kinks || [], interestRates: marketLow.interest_rates || [] }, calculateWalletBalance(walletBalancesMap, marketInfo.coinType, marketInfo.expo, price)), { retired: marketInfo.retired === true, ember: marketInfo.ember === true, isolated: marketInfo.isolated === true, deepbook: marketInfo.deepbook === true, safeBorrowLimit: (portfolioHighFreqData === null || portfolioHighFreqData === void 0 ? void 0 : portfolioHighFreqData.safe_borrow_limit) || '0', borrowLimitUsed: (portfolioHighFreqData === null || portfolioHighFreqData === void 0 ? void 0 : portfolioHighFreqData.borrow_limit_used) || '0', totalSuppliedUsd: (portfolioHighFreqData === null || portfolioHighFreqData === void 0 ? void 0 : portfolioHighFreqData.total_supplied_usd) || '0', tag: marketInfo.tag, tagVariant: marketInfo.tagVariant, tokenType: marketInfo.tokenType }));
        });
        let filteredTokens = tokens;
        if (searchTerm.trim()) {
            filteredTokens = filteredTokens.filter((token) => {
                var _a;
                const name = (_a = token.symbol) === null || _a === void 0 ? void 0 : _a.toLowerCase();
                const searchLower = searchTerm.toLowerCase();
                return name === null || name === void 0 ? void 0 : name.includes(searchLower);
            });
        }
        if (activeNavTab !== 'All') {
            filteredTokens = filteredTokens.filter((token) => {
                var _a;
                return (_a = token.tokenType) === null || _a === void 0 ? void 0 : _a.includes(activeNavTab.toLowerCase());
            });
        }
        return filteredTokens;
    }, [
        marketHighFreqData,
        marketLowFreqData,
        portfolioLowFreqData,
        portfolioHighFreqData,
        walletBalancesMap,
        searchTerm,
        activeNavTab,
    ]);
    const portfolioSections = useMemo(() => {
        const supplyTokens = [];
        const borrowTokens = [];
        if (!portfolioLowFreqData || !marketHighFreqData) {
            return { supply: { tokens: [] }, borrow: { tokens: [] } };
        }
        if (portfolioLowFreqData.supplied_amounts) {
            marketsArray.forEach((marketInfo) => {
                var _a, _b, _c, _d, _e, _f, _g;
                const marketId = marketInfo.id;
                const amount = portfolioLowFreqData.supplied_amounts[marketId]
                    ? new BigNumber(portfolioLowFreqData.supplied_amounts[marketId]).toFixed(marketInfo.expo)
                    : undefined;
                if (amount && parseFloat(amount) > 0) {
                    const marketHigh = (_a = marketHighFreqData.markets) === null || _a === void 0 ? void 0 : _a[marketId];
                    const marketLow = (_b = marketLowFreqData === null || marketLowFreqData === void 0 ? void 0 : marketLowFreqData.markets) === null || _b === void 0 ? void 0 : _b[marketId];
                    if (!marketInfo || (!marketHigh && !marketLow)) {
                        return;
                    }
                    const price = (marketHigh === null || marketHigh === void 0 ? void 0 : marketHigh.price) || '0';
                    const balanceUSD = (parseFloat(amount) * parseFloat(price)).toString();
                    const supplyApr = ((_c = marketLow === null || marketLow === void 0 ? void 0 : marketLow.supply_apr) === null || _c === void 0 ? void 0 : _c.total) || '0';
                    const supplyToolTipItems = generateSupplyTooltipItems(marketLow === null || marketLow === void 0 ? void 0 : marketLow.supply_apr, marketInfo.symbol);
                    const borrowedAmount = parseFloat(((_d = portfolioLowFreqData === null || portfolioLowFreqData === void 0 ? void 0 : portfolioLowFreqData.borrowed_amounts) === null || _d === void 0 ? void 0 : _d[marketId]) || '0');
                    const supplyCapReachedForPosition = isMarketSupplyCapReached(marketLow);
                    supplyTokens.push(Object.assign(Object.assign({ marketId, symbol: marketInfo.symbol, icon: getIcon(marketInfo.coinType === wbtc_layer0_coinType
                            ? 'wbtc_layer0'
                            : marketInfo.symbol.toLowerCase()), price, pausedTooltip: IS_WIND_DOWN_ACTIVE ? WIND_DOWN_MESSAGE : undefined, isSupplyEnabled: !IS_WIND_DOWN_ACTIVE && borrowedAmount === 0, isSupplyCapReached: borrowedAmount === 0 && supplyCapReachedForPosition, isBorrowEnabled: false, balance: amount, balanceUSD, supplyBalance: amount, supplyBalanceUSD: balanceUSD, apr: supplyApr, supplyApr, supplyToolTipItems: supplyToolTipItems, isSupplyRewardsEnabled: ((_e = marketLow === null || marketLow === void 0 ? void 0 : marketLow.supply_apr) === null || _e === void 0 ? void 0 : _e.rewards) && marketLow.supply_apr.rewards.length > 0, ltv: (marketLow === null || marketLow === void 0 ? void 0 : marketLow.ltv) || '0', borrowFee: (marketLow === null || marketLow === void 0 ? void 0 : marketLow.borrow_fee) || '0', liquidationThreshold: (marketLow === null || marketLow === void 0 ? void 0 : marketLow.liquidation_threshold) || '0', spreadFee: (marketLow === null || marketLow === void 0 ? void 0 : marketLow.spread_fee) || '0', borrowWeight: (marketLow === null || marketLow === void 0 ? void 0 : marketLow.borrow_weight) || '0', interestRateKinks: (marketLow === null || marketLow === void 0 ? void 0 : marketLow.interest_rate_kinks) || [], interestRates: (marketLow === null || marketLow === void 0 ? void 0 : marketLow.interest_rates) || [], allowedSupplyAmount: (marketLow === null || marketLow === void 0 ? void 0 : marketLow.allowed_supply_amount) || '0', allowedBorrowAmount: (marketLow === null || marketLow === void 0 ? void 0 : marketLow.allowed_borrow_amount) || '0', remainingInflowCapacity: (_f = marketHigh === null || marketHigh === void 0 ? void 0 : marketHigh.remaining_inflow_capacity) !== null && _f !== void 0 ? _f : null, remainingOutflowCapacity: (_g = marketHigh === null || marketHigh === void 0 ? void 0 : marketHigh.remaining_outflow_capacity) !== null && _g !== void 0 ? _g : null, totalSupply: (marketHigh === null || marketHigh === void 0 ? void 0 : marketHigh.total_supply) || '0', available: (marketHigh === null || marketHigh === void 0 ? void 0 : marketHigh.available_liquidity) || '0', totalBorrowed: calculateTotalBorrowed((marketHigh === null || marketHigh === void 0 ? void 0 : marketHigh.total_supply) || '0', (marketHigh === null || marketHigh === void 0 ? void 0 : marketHigh.available_liquidity) || '0'), utilizationRate: (marketLow === null || marketLow === void 0 ? void 0 : marketLow.utilization_rate) || '0', safeBorrowLimit: (portfolioHighFreqData === null || portfolioHighFreqData === void 0 ? void 0 : portfolioHighFreqData.safe_borrow_limit) || '0', borrowLimitUsed: (portfolioHighFreqData === null || portfolioHighFreqData === void 0 ? void 0 : portfolioHighFreqData.borrow_limit_used) || '0', totalSuppliedUsd: (portfolioHighFreqData === null || portfolioHighFreqData === void 0 ? void 0 : portfolioHighFreqData.total_supplied_usd) || '0', liquidationLimit: (portfolioHighFreqData === null || portfolioHighFreqData === void 0 ? void 0 : portfolioHighFreqData.liquidation_limit) || '0' }, calculateWalletBalance(walletBalancesMap, marketInfo.coinType, marketInfo.expo, price)), { retired: marketInfo.retired === true, tag: marketInfo.tag, tagVariant: marketInfo.tagVariant, tokenType: marketInfo.tokenType }));
                }
            });
        }
        if (portfolioLowFreqData.borrowed_amounts) {
            marketsArray.forEach((marketInfo) => {
                var _a, _b, _c, _d, _e, _f;
                const marketId = marketInfo.id;
                const amount = portfolioLowFreqData.borrowed_amounts[marketId]
                    ? new BigNumber(portfolioLowFreqData.borrowed_amounts[marketId]).toFixed(marketInfo.expo)
                    : undefined;
                if (amount && parseFloat(amount) > 0) {
                    const marketHigh = (_a = marketHighFreqData.markets) === null || _a === void 0 ? void 0 : _a[marketId];
                    const marketLow = (_b = marketLowFreqData === null || marketLowFreqData === void 0 ? void 0 : marketLowFreqData.markets) === null || _b === void 0 ? void 0 : _b[marketId];
                    if (!marketInfo || (!marketHigh && !marketLow)) {
                        return;
                    }
                    const price = (marketHigh === null || marketHigh === void 0 ? void 0 : marketHigh.price) || '0';
                    const balanceUSD = (parseFloat(amount) * parseFloat(price)).toString();
                    const borrowApr = ((_c = marketLow === null || marketLow === void 0 ? void 0 : marketLow.borrow_apr) === null || _c === void 0 ? void 0 : _c.total) || '0';
                    const borrowToolTipItems = generateBorrowTooltipItems(marketLow === null || marketLow === void 0 ? void 0 : marketLow.borrow_apr, marketInfo.symbol);
                    const borrowCapReachedForPosition = isMarketBorrowCapReached(marketLow);
                    borrowTokens.push(Object.assign(Object.assign({ marketId, symbol: marketInfo.symbol, icon: getIcon(marketInfo.coinType === wbtc_layer0_coinType
                            ? 'wbtc_layer0'
                            : marketInfo.symbol.toLowerCase()), price, pausedTooltip: IS_WIND_DOWN_ACTIVE ? WIND_DOWN_MESSAGE : undefined, isSupplyEnabled: false, isBorrowEnabled: !IS_WIND_DOWN_ACTIVE && !borrowCapReachedForPosition && !marketInfo.retired, isBorrowCapReached: borrowCapReachedForPosition && !marketInfo.retired, balance: amount, balanceUSD, borrowedBalance: amount, borrowedBalanceUSD: balanceUSD, apr: borrowApr, borrowApr, borrowToolTipItems: borrowToolTipItems, isBorrowRewardsEnabled: ((_d = marketLow === null || marketLow === void 0 ? void 0 : marketLow.borrow_apr) === null || _d === void 0 ? void 0 : _d.rewards) && marketLow.borrow_apr.rewards.length > 0, ltv: (marketLow === null || marketLow === void 0 ? void 0 : marketLow.ltv) || '0', borrowFee: (marketLow === null || marketLow === void 0 ? void 0 : marketLow.borrow_fee) || '0', liquidationThreshold: (marketLow === null || marketLow === void 0 ? void 0 : marketLow.liquidation_threshold) || '0', spreadFee: (marketLow === null || marketLow === void 0 ? void 0 : marketLow.spread_fee) || '0', borrowWeight: (marketLow === null || marketLow === void 0 ? void 0 : marketLow.borrow_weight) || '0', interestRateKinks: (marketLow === null || marketLow === void 0 ? void 0 : marketLow.interest_rate_kinks) || [], interestRates: (marketLow === null || marketLow === void 0 ? void 0 : marketLow.interest_rates) || [], allowedSupplyAmount: (marketLow === null || marketLow === void 0 ? void 0 : marketLow.allowed_supply_amount) || '0', allowedBorrowAmount: (marketLow === null || marketLow === void 0 ? void 0 : marketLow.allowed_borrow_amount) || '0', remainingInflowCapacity: (_e = marketHigh === null || marketHigh === void 0 ? void 0 : marketHigh.remaining_inflow_capacity) !== null && _e !== void 0 ? _e : null, remainingOutflowCapacity: (_f = marketHigh === null || marketHigh === void 0 ? void 0 : marketHigh.remaining_outflow_capacity) !== null && _f !== void 0 ? _f : null, totalSupply: (marketHigh === null || marketHigh === void 0 ? void 0 : marketHigh.total_supply) || '0', available: (marketHigh === null || marketHigh === void 0 ? void 0 : marketHigh.available_liquidity) || '0', totalBorrowed: calculateTotalBorrowed((marketHigh === null || marketHigh === void 0 ? void 0 : marketHigh.total_supply) || '0', (marketHigh === null || marketHigh === void 0 ? void 0 : marketHigh.available_liquidity) || '0'), utilizationRate: (marketLow === null || marketLow === void 0 ? void 0 : marketLow.utilization_rate) || '0', safeBorrowLimit: (portfolioHighFreqData === null || portfolioHighFreqData === void 0 ? void 0 : portfolioHighFreqData.safe_borrow_limit) || '0', borrowLimitUsed: (portfolioHighFreqData === null || portfolioHighFreqData === void 0 ? void 0 : portfolioHighFreqData.borrow_limit_used) || '0', totalSuppliedUsd: (portfolioHighFreqData === null || portfolioHighFreqData === void 0 ? void 0 : portfolioHighFreqData.total_supplied_usd) || '0', liquidationLimit: (portfolioHighFreqData === null || portfolioHighFreqData === void 0 ? void 0 : portfolioHighFreqData.liquidation_limit) || '0' }, calculateWalletBalance(walletBalancesMap, marketInfo.coinType, marketInfo.expo, price)), { retired: marketInfo.retired === true, tag: marketInfo.tag, tagVariant: marketInfo.tagVariant, tokenType: marketInfo.tokenType }));
                }
            });
        }
        return {
            supply: { tokens: supplyTokens },
            borrow: { tokens: borrowTokens },
        };
    }, [
        portfolioLowFreqData,
        marketHighFreqData,
        marketLowFreqData,
        portfolioHighFreqData,
        walletBalancesMap,
    ]);
    const [marketTableSortConfig, setMarketTableSortConfig] = useState({ key: 'symbol', direction: null });
    const handleMarketTableSort = createSortHandler(setMarketTableSortConfig);
    const handleSupplySort = createSortHandler(setSupplySortConfig);
    const handleBorrowSort = createSortHandler(setBorrowSortConfig);
    const activeMarketTokens = useMemo(() => allMarketTokens.filter((token) => !token.ember && !token.retired && !token.isolated && !token.deepbook), [allMarketTokens]);
    const sortedMarketTableTokens = useMemo(() => sortTokens(activeMarketTokens, marketTableSortConfig), [activeMarketTokens, marketTableSortConfig]);
    const sortedSupplyTokens = useMemo(() => sortTokens(portfolioSections.supply.tokens, supplySortConfig), [portfolioSections.supply.tokens, supplySortConfig]);
    const sortedBorrowTokens = useMemo(() => sortTokens(portfolioSections.borrow.tokens, borrowSortConfig), [portfolioSections.borrow.tokens, borrowSortConfig]);
    const collateralTokens = useMemo(() => {
        return portfolioSections.supply.tokens.flatMap((supply) => {
            var _a, _b;
            const marketInfo = marketsArray.find((m) => m.id === supply.marketId);
            if (!marketInfo)
                return [];
            const usdValueNum = parseFloat(supply.supplyBalanceUSD || '0');
            return [
                {
                    symbol: supply.symbol,
                    icon: supply.icon,
                    balance: supply.supplyBalance || '0',
                    price: supply.price,
                    usdValueNum,
                    marketId: supply.marketId,
                    coinType: marketInfo.coinType,
                    expo: marketInfo.expo,
                    ltv: (_a = supply.ltv) !== null && _a !== void 0 ? _a : '0',
                    liquidationThreshold: (_b = supply.liquidationThreshold) !== null && _b !== void 0 ? _b : '0',
                },
            ];
        });
    }, [portfolioSections.supply.tokens]);
    useEffect(() => {
        if (!modalState.isOpen || modalState.actionType !== 'Repay' || collateralTokens.length === 0) {
            return;
        }
        const current = selectedCollateralToken;
        const isCurrentInList = current && collateralTokens.some((t) => t.marketId === current.marketId);
        if (!current || !isCurrentInList) {
            setSelectedCollateralToken(collateralTokens[0]);
        }
    }, [modalState.isOpen, modalState.actionType, collateralTokens, selectedCollateralToken]);
    // Set auto slippage when "use collateral" is toggled on or collateral token changes (Repay tab)
    const autoCollateralSymbol = (_a = (selectedCollateralToken !== null && selectedCollateralToken !== void 0 ? selectedCollateralToken : collateralTokens[0])) === null || _a === void 0 ? void 0 : _a.symbol;
    useEffect(() => {
        var _a, _b;
        if (modalState.isOpen &&
            modalState.actionType === 'Repay' &&
            useCollateral &&
            autoCollateralSymbol) {
            setSlippageValue(getAutoSlippage(autoCollateralSymbol, (_b = (_a = modalState.token) === null || _a === void 0 ? void 0 : _a.symbol) !== null && _b !== void 0 ? _b : undefined));
        }
    }, [
        modalState.isOpen,
        modalState.actionType,
        (_b = modalState.token) === null || _b === void 0 ? void 0 : _b.symbol,
        useCollateral,
        autoCollateralSymbol,
    ]);
    const collapsibleSections = useMemo(() => {
        const sections = [];
        if (allMarketTokens.filter((token) => token.deepbook === true).length > 0) {
            sections.push({
                key: 'deepbook',
                title: 'DeepBook Markets',
                tokens: sortTokens(allMarketTokens.filter((token) => token.deepbook === true), marketTableSortConfig),
                defaultExpanded: true,
            });
        }
        if (allMarketTokens.filter((token) => token.isolated === true).length > 0) {
            sections.push({
                key: 'isolated',
                title: 'Isolated Markets',
                tokens: sortTokens(allMarketTokens.filter((token) => token.isolated === true), marketTableSortConfig),
                defaultExpanded: false,
            });
        }
        if (allMarketTokens.filter((token) => token.ember === true).length > 0) {
            sections.push({
                key: 'ember',
                title: 'Ember Markets',
                tokens: sortTokens(allMarketTokens.filter((token) => token.ember === true), marketTableSortConfig),
                defaultExpanded: false,
            });
        }
        if (allMarketTokens.filter((token) => token.retired === true).length > 0) {
            const retiredTokens = allMarketTokens.filter((token) => token.retired === true);
            const sortedRetiredTokens = retiredTokens.sort((a, b) => {
                const aHasBothEnabled = a.isSupplyEnabled && a.isBorrowEnabled;
                const bHasBothEnabled = b.isSupplyEnabled && b.isBorrowEnabled;
                if (aHasBothEnabled && !bHasBothEnabled)
                    return -1;
                if (!aHasBothEnabled && bHasBothEnabled)
                    return 1;
                return 0;
            });
            sections.push({
                key: 'retired',
                title: 'Retired Markets',
                tokens: sortTokens(sortedRetiredTokens, marketTableSortConfig),
                defaultExpanded: false,
            });
        }
        return sections;
    }, [allMarketTokens, marketTableSortConfig]);
    const getRewardsDistribution = () => {
        if (!(portfolioHighFreqData === null || portfolioHighFreqData === void 0 ? void 0 : portfolioHighFreqData.rewards_by_token))
            return [];
        return Object.entries(portfolioHighFreqData.rewards_by_token)
            .map(([coinType, amount]) => {
            var _a;
            const marketInfo = marketsArray.find((m) => m.coinType === coinType);
            if (!marketInfo) {
                console.warn(`Unknown reward token: ${coinType}`);
                return null;
            }
            const marketData = (_a = marketHighFreqData === null || marketHighFreqData === void 0 ? void 0 : marketHighFreqData.markets) === null || _a === void 0 ? void 0 : _a[marketInfo.id];
            const tokenPrice = (marketData === null || marketData === void 0 ? void 0 : marketData.price) ? parseFloat(marketData.price) : 0;
            const tokenAmount = parseFloat(amount);
            const usdValue = tokenAmount * tokenPrice;
            return {
                label: marketInfo.symbol,
                amount: amount,
                usdAmount: usdValue.toString(),
                icon: getIcon(marketInfo.symbol.toLowerCase()),
            };
        })
            .filter((entry) => entry !== null)
            .sort((a, b) => a.label.localeCompare(b.label));
    };
    // Callback to update validation token (memoized to prevent unnecessary re-renders)
    const handleValidateSlippageCallback = useCallback((token) => {
        setLastValidatedToken(token);
    }, []);
    useEffect(() => {
        if (slippageIntervalRef.current) {
            clearInterval(slippageIntervalRef.current);
            slippageIntervalRef.current = null;
        }
        if (lastValidatedToken && address) {
            // Initial validation - show loader
            handleValidateSlippage(lastValidatedToken, true);
            // Set up 10-second interval - don't show loader for periodic checks
            slippageIntervalRef.current = setInterval(() => {
                handleValidateSlippage(lastValidatedToken, false);
            }, 10000);
        }
        else {
            setSlippageError(null);
            setIsValidatingSlippage(false);
        }
        return () => {
            if (slippageIntervalRef.current) {
                clearInterval(slippageIntervalRef.current);
                slippageIntervalRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastValidatedToken, address]);
    const handleRowClick = (token) => {
        const marketData = token;
        navigate(`/market/${marketData.symbol}`, {
            state: { marketId: marketData.marketId },
        });
    };
    const fetchTokensWithPrices = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const tokens = buildTokensList(coinInfoData || undefined, walletBalancesMap);
            setTokens(tokens);
            return tokens;
        }
        catch (error) {
            console.error('Error fetching tokens with prices:', error);
            return [];
        }
    }), [coinInfoData, walletBalancesMap]);
    // Fetch tokens on component mount and when wallet balances change
    useEffect(() => {
        fetchTokensWithPrices();
    }, [fetchTokensWithPrices]);
    const formatTokenDataForMarketModal = (token) => {
        var _a, _b, _c;
        if (!token)
            return null;
        const targetMarket = modalState.token ? prepareTokenForModal(modalState.token) : null;
        if (!targetMarket) {
            return {
                symbol: token.symbol,
                icon: token.icon,
                price: (_a = token.price) !== null && _a !== void 0 ? _a : '0',
                walletBalance: (_b = token.walletBalance) !== null && _b !== void 0 ? _b : '0',
                marketId: '',
                isSupplyEnabled: !IS_WIND_DOWN_ACTIVE,
                isBorrowEnabled: false,
            };
        }
        return Object.assign(Object.assign({}, targetMarket), { walletBalance: (_c = token.walletBalance) !== null && _c !== void 0 ? _c : '0', inWallet: token.walletBalance, price: token.price || '0', symbol: token.symbol });
    };
    const DAILY_EARNINGS_TOOLTIP = `
  <div style="font-family: system-ui, sans-serif; font-weight: bolder; margin-bottom: 0.33vw;">
  Daily Earnings = Daily Supply Interest − Daily Borrow<br/>Interest + Daily Rewards.
  </div>
  <div style="font-family: system-ui, sans-serif; font-weight: 300;">
  Supply Interest auto-compounds to your balance.<br/>
  Rewards requires manual claiming via "Claim Rewards".
  </div>
 `;
    const handleTabClick = (tab) => {
        setActiveNavTab(tab);
        if (tab === 'All') {
            setSearchParams({});
        }
        else {
            setSearchParams({ tab: tab.toLowerCase() });
        }
    };
    // Memoize reward instructions to prevent unnecessary re-renders
    const rewardInstructions = useMemo(() => [
        {
            text: 'Swap to',
            coinOptions: [
                { label: 'USDC' },
                { label: 'suiUSDT' },
                { label: 'stSUI' },
                { label: 'SUI' },
            ],
        },
        {
            text: 'Supply/Repay on Lend',
        },
    ], []);
    return (_jsxs("div", { className: "theme-bg-lndBody", children: [_jsx(Portfolio, { isWalletConnected: address ? true : false, title: "Supply & Borrow", globalStats: {
                    availableLiquidity: (parseFloat((marketHighFreqData === null || marketHighFreqData === void 0 ? void 0 : marketHighFreqData.total_supplied_usd) || '0') -
                        parseFloat((marketHighFreqData === null || marketHighFreqData === void 0 ? void 0 : marketHighFreqData.total_borrowed_usd) || '0')).toString(),
                    supplied: (marketHighFreqData === null || marketHighFreqData === void 0 ? void 0 : marketHighFreqData.total_supplied_usd) || '0',
                    borrowed: (marketHighFreqData === null || marketHighFreqData === void 0 ? void 0 : marketHighFreqData.total_borrowed_usd) || '0',
                }, personalStats: {
                    netWorth: (portfolioHighFreqData === null || portfolioHighFreqData === void 0 ? void 0 : portfolioHighFreqData.net_worth) || '0',
                    netWorthUnparsed: `$${parseFloat((portfolioHighFreqData === null || portfolioHighFreqData === void 0 ? void 0 : portfolioHighFreqData.net_worth) || '0').toFixed(2)}`,
                    dailyEarnings: (portfolioLowFreqData === null || portfolioLowFreqData === void 0 ? void 0 : portfolioLowFreqData.daily_earnings) || '0',
                    netApr: (portfolioLowFreqData === null || portfolioLowFreqData === void 0 ? void 0 : portfolioLowFreqData.net_apr) || '0',
                    borrowLimitUsed: (portfolioHighFreqData === null || portfolioHighFreqData === void 0 ? void 0 : portfolioHighFreqData.borrow_limit_used) || '0',
                    safeBorrowLimit: (portfolioHighFreqData === null || portfolioHighFreqData === void 0 ? void 0 : portfolioHighFreqData.safe_borrow_limit) || '0',
                    liquidationLimit: (portfolioHighFreqData === null || portfolioHighFreqData === void 0 ? void 0 : portfolioHighFreqData.liquidation_limit) || '0',
                    pendingRewards: (portfolioHighFreqData === null || portfolioHighFreqData === void 0 ? void 0 : portfolioHighFreqData.rewards_to_claim_usd) || '0',
                    borrowLimitUsedPercentage: (parseFloat((portfolioHighFreqData === null || portfolioHighFreqData === void 0 ? void 0 : portfolioHighFreqData.borrow_limit_used) || '0') /
                        parseFloat((portfolioHighFreqData === null || portfolioHighFreqData === void 0 ? void 0 : portfolioHighFreqData.total_supplied_usd) || '1')) *
                        100,
                    safeLimitPercentage: (portfolioHighFreqData === null || portfolioHighFreqData === void 0 ? void 0 : portfolioHighFreqData.safe_borrow_limit) && (portfolioHighFreqData === null || portfolioHighFreqData === void 0 ? void 0 : portfolioHighFreqData.total_supplied_usd)
                        ? (parseFloat((portfolioHighFreqData === null || portfolioHighFreqData === void 0 ? void 0 : portfolioHighFreqData.safe_borrow_limit) || '0') /
                            parseFloat((portfolioHighFreqData === null || portfolioHighFreqData === void 0 ? void 0 : portfolioHighFreqData.total_supplied_usd) || '1')) *
                            100
                        : undefined,
                    liquidationPercentage: (portfolioHighFreqData === null || portfolioHighFreqData === void 0 ? void 0 : portfolioHighFreqData.liquidation_limit) && (portfolioHighFreqData === null || portfolioHighFreqData === void 0 ? void 0 : portfolioHighFreqData.total_supplied_usd)
                        ? (parseFloat((portfolioHighFreqData === null || portfolioHighFreqData === void 0 ? void 0 : portfolioHighFreqData.liquidation_limit) || '0') /
                            parseFloat((portfolioHighFreqData === null || portfolioHighFreqData === void 0 ? void 0 : portfolioHighFreqData.total_supplied_usd) || '1')) *
                            100
                        : undefined,
                    rewards: getRewardsDistribution(),
                    rewardInstructions: rewardInstructions,
                    pendingRewardsUsd: (portfolioHighFreqData === null || portfolioHighFreqData === void 0 ? void 0 : portfolioHighFreqData.rewards_to_claim_usd) || '0',
                }, closeIcon: getThemeIcon(themeName, 'darkCrossIcon', 'close'), hoverTexts: {
                    'Daily Earnings': DAILY_EARNINGS_TOOLTIP,
                }, infoIcon: getIcon('infoIcon'), onClaimRewards: handleClaimRewards, onValidateSlippage: handleValidateSlippageCallback, slippageError: slippageError, isValidatingSlippage: isValidatingSlippage, wordSpacing: "0.2vw", walletConnectFunctionality: walletConnectFunctionality, checkmarkIcon: getIcon('checkMarkIcon'), disableRewards: !address || parseFloat((portfolioHighFreqData === null || portfolioHighFreqData === void 0 ? void 0 : portfolioHighFreqData.rewards_to_claim_usd) || '0') === 0 }), address && (_jsx("div", { className: `md:max-w-[1390px] mx-auto justify-center ${screenWidth.width <= 1024 ? 'px-[20px]' : ''} ${screenWidth.width < 728 ? 'px-[20px] pt-[20px]' : 'pt-4'}`, children: _jsx(AvailableLiquidity, { totalLiquidityTitle: "Available Liquidity", supplyLiquidityTitle: "Supplied", borrowLiquidityTitle: "Borrowed", totalLiquidityValue: (parseFloat((marketHighFreqData === null || marketHighFreqData === void 0 ? void 0 : marketHighFreqData.total_supplied_usd) || '0') -
                        parseFloat((marketHighFreqData === null || marketHighFreqData === void 0 ? void 0 : marketHighFreqData.total_borrowed_usd) || '0')).toString(), supplyLiquidityValue: (marketHighFreqData === null || marketHighFreqData === void 0 ? void 0 : marketHighFreqData.total_supplied_usd) || '0', borrowLiquidityValue: (marketHighFreqData === null || marketHighFreqData === void 0 ? void 0 : marketHighFreqData.total_borrowed_usd) || '0', expandIcon: getThemeIcon(themeName, 'whiteExpandIcon', 'defaultThemeExpandIcon'), shrinkIcon: getThemeIcon(themeName, 'darkShrinkIcon', 'defaultShrinkIcon') }) })), address && (_jsxs("div", { className: `flex justify-between md:max-w-[1390px] md:gap-4 mx-auto pt-[13.6px] md:pt-[19.2px] flex-col md:flex-row px-5 2xl:px-[unset]`, children: [portfolioSections.supply.tokens.length > 0 && (_jsx("div", { className: `${screenWidth.width < 768 ? 'w-full' : 'w-[50%]'} ${screenWidth.width === 768 ? 'md:w-[360px]' : ''}`, children: _jsx(PositionCard, { title: "Your Supplies", headerData: {
                                balance: (portfolioHighFreqData === null || portfolioHighFreqData === void 0 ? void 0 : portfolioHighFreqData.total_supplied_usd) || '0',
                                apr: (portfolioLowFreqData === null || portfolioLowFreqData === void 0 ? void 0 : portfolioLowFreqData.aggregated_supply_apr) || '0',
                            }, tokens: sortedSupplyTokens, actions: [
                                {
                                    label: 'supply',
                                    onClick: handleSupply,
                                    variant: 'primary',
                                },
                                {
                                    label: 'withdraw',
                                    onClick: handleWithdraw,
                                    variant: 'secondary',
                                },
                            ], sortConfig: supplySortConfig, onSort: handleSupplySort, ArrowUp: getThemeIcon(themeName, 'darkThemeUpArrow', 'upArrowBlack'), ArrowDown: getThemeIcon(themeName, 'darkThemeDownArrow', 'downArrowBlack'), defaultArrow: getThemeIcon(themeName, 'darkThemeDefaultArrow', 'defaultArrow'), plusIcon: getIcon('plusIcon'), minusIcon: getIcon('minusIcon'), columnLabels: {
                                symbol: 'Assets',
                                balanceUSD: 'Balance',
                                apr: 'APR',
                            }, isSupply: true, onRowClick: handleRowClick }) })), portfolioSections.borrow.tokens.length > 0 && (_jsx("div", { className: `${screenWidth.width < 768 ? 'w-full' : 'w-[50%]'} ${screenWidth.width === 768 ? 'md:w-[360px]' : ''}`, children: _jsx(PositionCard, { title: "Your Borrows", headerData: {
                                balance: (portfolioHighFreqData === null || portfolioHighFreqData === void 0 ? void 0 : portfolioHighFreqData.total_borrowed_usd) || '0',
                                apr: (portfolioLowFreqData === null || portfolioLowFreqData === void 0 ? void 0 : portfolioLowFreqData.aggregated_borrow_apr) || '0',
                            }, tokens: sortedBorrowTokens, actions: [
                                {
                                    label: 'borrow',
                                    onClick: handleBorrow,
                                    variant: 'primary',
                                },
                                {
                                    label: 'repay',
                                    onClick: handleRepay,
                                    variant: 'secondary',
                                },
                            ], sortConfig: borrowSortConfig, onSort: handleBorrowSort, ArrowUp: getThemeIcon(themeName, 'darkThemeUpArrow', 'upArrowBlack'), ArrowDown: getThemeIcon(themeName, 'darkThemeDownArrow', 'downArrowBlack'), defaultArrow: getThemeIcon(themeName, 'darkThemeDefaultArrow', 'defaultArrow'), plusIcon: getIcon('plusIcon'), minusIcon: getIcon('minusIcon'), columnLabels: {
                                symbol: 'Assets',
                                balanceUSD: 'Debt',
                                apr: 'APR',
                            }, isSupply: false, onRowClick: handleRowClick }) }))] })), _jsxs("div", { className: "flex lg:flex-row lg:max-w-[985px] xl:max-w-[1390px] w-full flex-col-reverse justify-center items-start md:items-center md:justify-between mx-auto px-5 md:px-[unset] md:mt-[35px] mt-[20px]", children: [_jsx(FilterTab, { tabs: AVAILABLE_TABS.filter((tab) => tab !== 'Retired'), activeTab: activeNavTab, handleTabClick: handleTabClick }), _jsx("div", { className: "flex justify-end w-full md:px-[unset] mb-[26px] lg:mb-[unset] md:mr-[34px] lg:mr-[unset] mr-[unset]", children: _jsx(SearchInput, { search: searchTerm, handleSearchChange: (e) => setSearchTerm(e.target.value), clearSearch: () => setSearchTerm(''), clearSearchIcon: getIcon('clearSearchIcon'), searchIcon: themeName === 'dark'
                                ? getIcon('darkThemeSearchIcon')
                                : getIcon('defaultThemeSearchIcon') }) })] }), _jsx("div", { className: `md:max-w-[1390px] mx-auto pb-[190px] px-5 xl:px-[unset]`, children: _jsx(MarketTable, { tokens: sortedMarketTableTokens, sortUpIcon: getThemeIcon(themeName, 'darkThemeUpArrow', 'upArrowBlack'), sortDownIcon: getThemeIcon(themeName, 'darkThemeDownArrow', 'downArrowBlack'), sortDefaultIcon: getThemeIcon(themeName, 'darkThemeDefaultArrow', 'defaultArrow'), activeFilter: "All", onFilterChange: () => { }, onSupply: handleSupply, onBorrow: handleBorrow, sortConfig: marketTableSortConfig, onSort: handleMarketTableSort, collapsibleSections: collapsibleSections, isWalletConnected: address ? true : false, walletConnectFunctionality: walletConnectFunctionality, onRowClick: handleRowClick }) }), _jsx(TokenActionModal, { isOpen: modalState.isOpen, token: prepareTokenForModal(modalState.token), onClose: handleCloseModal, onSubmit: handleSubmit, activeTab: activeTab, setActiveTab: setActiveTab, setIsTransactionLoading: setIsTransactionLoading, isTransactionLoading: isTransactionLoading, isWalletConnected: !!address, rightArrow: getThemeIcon(themeName, 'darkThemeRightArrow', 'rightArrow'), resetInputTrigger: resetInputTrigger, clearSearchIcon: getIcon('clearSearchIcon'), searchIcon: themeName === 'dark' ? getIcon('searchDark') : getIcon('defaultThemeSearchIcon'), backIcon: themeName === 'dark' ? getIcon('whiteBackIcon') : getIcon('blackBackIcon'), payToken: (() => {
                    const marketInfo = marketsArray.find((m) => { var _a; return m.id === ((_a = modalState.token) === null || _a === void 0 ? void 0 : _a.marketId); });
                    return marketInfo
                        ? tokens.find((t) => t.tokenId === marketInfo.coinType)
                        : tokens[0];
                })(), tokens: tokens, onPayTokenSelect: handlePayTokenSelect, defaultToken: (() => {
                    const marketInfo = marketsArray.find((m) => { var _a; return m.id === ((_a = modalState.token) === null || _a === void 0 ? void 0 : _a.marketId); });
                    return marketInfo
                        ? tokens.find((t) => (t.symbol !== 'SUI' && t.tokenId === marketInfo.coinType) ||
                            (t.symbol === 'SUI' && marketInfo.coinType === '0x2::sui::SUI'))
                        : tokens[0];
                })(), selectedToken: selectedSwapSupplyToken || undefined, setSwapAndPayAmount: setSwapAndPayAmount, selectedTokenInModalFormat: formatTokenDataForMarketModal(selectedSwapSupplyToken), swapQuoteObject: swapQuoteObject, resetQuoteData: resetQuoteData, viceVersaIcon: themeName === 'dark' ? getIcon('viceVersaIconWhite') : getIcon('viceVersaIconBlack'), downArrow: themeName === 'dark' ? getIcon('whiteDownArrow') : getIcon('blackDownArrow'), zapRightArrow: themeName === 'dark' ? getIcon('darkThemeRightArrow') : getIcon('zapDefaultRightArrow'), slippageIcon: themeName === 'dark' ? getIcon('whiteSlippageIcon') : getIcon('blackSlippageIcon'), slippageValue: slippageValue, onSlippageChange: setSlippageValue, newQuoteArrived: newQuoteArrived, isQuoteStale: isQuoteStale, isQuoteLoading: isQuoteLoading, collateralTokens: collateralTokens, onCollateralTokenSelect: setSelectedCollateralToken, selectedCollateralToken: selectedCollateralToken !== null && selectedCollateralToken !== void 0 ? selectedCollateralToken : undefined, isFlashRepaySupported: isFlashRepaySupported, flashRepayFeeDecimal: naviFlashLoanFee, flashRepayMaxRepayAmount: flashRepayMaxRepayAmount !== null && flashRepayMaxRepayAmount !== void 0 ? flashRepayMaxRepayAmount : null, flashRepayQuoteObject: flashRepayQuoteObject, isFlashRepayQuoteStale: isFlashRepayQuoteStale, isFlashRepayQuoteLoading: isFlashRepayQuoteLoading, flashRepayNewQuoteArrived: flashRepayNewQuoteArrived, onFlashRepaySwapAmountChange: setFlashRepaySwapAmount, onUseCollateralChange: setUseCollateral }), _jsx(SwapButton, { isSwapModalOpen: isSwapModalOpen, onClick: () => setIsSwapModalOpen(true), themeName: themeName, isTokenActionModalOpen: modalState.isOpen }), _jsx(SwapModal, { isOpen: isSwapModalOpen, onClose: () => setIsSwapModalOpen(false), onNotification: handleSwapNotification, loading: isTransactionLoading, setLoading: setIsTransactionLoading, walletConnectFunctionality: walletConnectFunctionality, isWalletConnected: !!address, theme: themeName }), _jsx(NotificationComp, { isOpen: isNotificationOpen, setIsOpen: handleNotificationClose, info: notificationInfo })] }));
};
export default Markets;
